import {
    Checkbox,
    InputAdornment,
    Paper,
    TableBody,
    TableCell,
    TableRow,
    Toolbar,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import { DeleteForeverOutlined, Search } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/Edit';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Controls } from '../../components/controls/Controls';
import PageHeader from '../../components/PageHeader';
import Popup from '../../components/Popup';
import useTable from '../../components/useTable';
import { loadCuentasBancarias, saveCuentaBancaria } from '../../redux/ducks/cuentasBancarias';
import { loadBancos } from '../../redux/ducks/bancos';
import CuentasBancariasForme from './CuentasBancariasForme';
import { setIndex } from '../../redux/ducks/sideMenuIndex';
import { filterOnAnyField } from '../../utils';
import ConfirmDialog from '../../components/ConfirmDialog';
import http from '../../http-common';

const useStyles = makeStyles((theme) => ({
    pageContent: {
        margin: theme.spacing(5),
        padding: theme.spacing(3),
    },
    searchInput: {
        width: '75%',
    },
    newButton: {},
    refreshButton: {},
}));

const headCells = [
    { id: 'id', label: 'ID' },
    { id: 'titular', label: 'Titular' },
    { id: 'idBanco', label: 'Banco' },
    { id: 'numeroCuenta', label: 'Número de Cuenta' },
    { id: 'clabe', label: 'CLABE / IBAN' },
    { id: 'tipoCuenta', label: 'Tipo de Cuenta' },
    { id: 'moneda', label: 'Moneda' },
    { id: 'saldoInicial', label: 'Saldo Inicial' },
    { id: 'activa', label: 'Activa' },
    { id: 'cuentaDefault', label: 'Cuenta Default', disableSorting: true },
    { id: 'actions', label: 'Acciones', disableSorting: true },
];

const formatMoneda = (value) => {
    if (value == null) return '$ 0.00';
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
    }).format(value);
};

export default function CuentasBancarias() {
    const [openPopup, setOpenPopup] = useState(false);
    const classes = useStyles();
    const [filterFn, setFilterFn] = useState({ fn: (items) => items });
    const [recordForEdit, setRecordForEdit] = useState();

    const cuentasBancarias = useSelector((state) => state.cuentasBancarias.cuentasBancarias);
    const bancos = useSelector((state) => state.bancos.bancos);

    const {
        TblContainer,
        TblHead,
        TblPagination,
        recordsAfterPagingAndSorting,
    } = useTable(cuentasBancarias, headCells, filterFn, 100, 'titular', 'asc');

    const [filter, setFilter] = useState();

    const handleSearch = (e) => {
        const { target } = e;
        setFilter(target.value);
        setFilterFn({
            fn: (items) => {
                if (target.value === '') return items;
                return filterOnAnyField(items, target.value.toLowerCase());
            },
        });
    };

    const dispatch = useDispatch();

    const addOrEdit = (cuentaBancaria, resetForm) => {
        dispatch(saveCuentaBancaria(cuentaBancaria));
        setOpenPopup(false);
        resetForm();
    };

    const openInPopup = (item) => {
        setRecordForEdit(item);
        setOpenPopup(true);
    };

    useEffect(() => {
        dispatch(loadCuentasBancarias());
        dispatch(loadBancos());
    }, [dispatch]);

    useEffect(() => {
        dispatch(setIndex(2, 'Cat'));
    }, [dispatch]);

    const [confirmDialog, setConfirmDialog] = useState({
        title: 'title',
        subtitle: 'subtitle',
        isOpen: false,
    });

    const deleteCuentaBancaria = (item) => {
        setConfirmDialog({
            subtitle: '',
            title: '¿Desea borrar la cuenta bancaria?',
            isOpen: true,
            onConfirm: async () => {
                await http.delete(`/cuentas-bancarias/${item.id}`);
                dispatch(loadCuentasBancarias());
            },
        });
    };

    // Helper: get bank name from id
    const getBancoNombre = (idBanco) => {
        const found = bancos.find((b) => b.id === idBanco);
        return found ? found.nombre : idBanco;
    };

    return (
        <>
            <PageHeader
                icon={<AccountBalanceWalletOutlinedIcon fontSize='large' />}
                subtitle=''
                title='Cuentas Bancarias'
            />
            <Paper className={classes.pageContent}>
                <Toolbar>
                    <Controls.Input
                        className={classes.searchInput}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='end'>
                                    <Search />
                                </InputAdornment>
                            ),
                        }}
                        label='Buscar'
                        onChange={handleSearch}
                        value={filter}
                    />
                    <Controls.Button
                        className={classes.refreshButton}
                        onClick={() => dispatch(loadCuentasBancarias())}
                        startIcon={<RefreshIcon />}
                        text='Refrescar'
                        variant='outlined'
                    />
                    <Controls.Button
                        className={classes.newButton}
                        onClick={() => {
                            setRecordForEdit(null);
                            setOpenPopup(true);
                        }}
                        startIcon={<AddIcon />}
                        text='Agregar Nueva'
                        variant='outlined'
                    />
                </Toolbar>
                <TblContainer>
                    <TblHead />
                    <TableBody>
                        {recordsAfterPagingAndSorting().map((item) => (
                            <TableRow key={item.id}>
                                <TableCell>{item.id}</TableCell>
                                <TableCell>{item.titular}</TableCell>
                                <TableCell>{getBancoNombre(item.idBanco)}</TableCell>
                                <TableCell>{item.numeroCuenta}</TableCell>
                                <TableCell>{item.clabe}</TableCell>
                                <TableCell>{item.tipoCuenta}</TableCell>
                                <TableCell>{item.moneda}</TableCell>
                                <TableCell align='right'>{formatMoneda(item.saldoInicial)}</TableCell>
                                <TableCell>{item.activa ? 'Sí' : 'No'}</TableCell>
                                <TableCell padding='checkbox'>
                                    <Checkbox checked={!!item.cuentaDefault} disabled />
                                </TableCell>
                                <TableCell>
                                    <Controls.ActionButton
                                        color='primary'
                                        onClick={() => openInPopup(item)}
                                    >
                                        <EditOutlinedIcon fontSize='small' />
                                    </Controls.ActionButton>
                                    <Controls.ActionButton
                                        color='primary'
                                        onClick={() => deleteCuentaBancaria(item)}
                                    >
                                        <DeleteForeverOutlined fontSize='small' />
                                    </Controls.ActionButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </TblContainer>
                <TblPagination />
            </Paper>
            <Popup
                openPopup={openPopup}
                setOpenPopup={setOpenPopup}
                title='Cuenta Bancaria'
            >
                <CuentasBancariasForme
                    addOrEdit={addOrEdit}
                    bancos={bancos}
                    recordForEdit={recordForEdit}
                />
            </Popup>
            <ConfirmDialog
                confirmDialog={confirmDialog}
                setConfirmDialog={setConfirmDialog}
            />
        </>
    );
}
